import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { QnSbnService } from '../services/qn-sbn-service.service';
import { OneSnackBarService, OneSnackBarType } from '@one/angular-kit/modal';
import { FileFormatDialogComponent } from '../notification/file-format-dialog/file-format-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import * as XLSX from 'xlsx';
import { NativeDateAdapter, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { formatDate } from '@angular/common';

export const PICK_FORMATS = {
  parse: { dateInput: { month: 'short', year: 'numeric', day: 'numeric' } },
  display: {
    dateInput: 'input',
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
}

@Injectable()
class PickDateAdapter extends NativeDateAdapter {
  public format(date: Date, displayFormat: Object): string {
    if (displayFormat === 'input') {
      return formatDate(date, 'dd-MMM-yyyy', this.locale);
    } else {
      return date.toDateString();
    }
  }
}

function dateRangeValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const from = control.get('dateFrom')?.value;
    const to = control.get('dateTo')?.value;
    if (from && to && from > to) {
      return { dateRangeInvalid: true };
    }
    return null;
  };
}

function formatDateOnly(dateValue: Date): string {
  if (dateValue) {
    const date = new Date(dateValue);
    const day = date.toLocaleString('default', { day: '2-digit' });
    const month = date.toLocaleString('default', { month: '2-digit' });
    const year = date.toLocaleString('default', { year: 'numeric' });
    return year + '-' + month + '-' + day;
  } else {
    return '';
  }
}

@Component({
  selector: 'dl-email-delivery',
  templateUrl: './email-delivery.component.html',
  styleUrls: ['./email-delivery.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: PickDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PICK_FORMATS }
  ]
})
export class EmailDeliveryComponent implements OnInit {

  @ViewChild('delivered') delivered: ElementRef;
  @ViewChild('failed') failed: ElementRef;
  deliveredPercent = 0;
  failedPercent = 0;
  deliveredTitle = `Delivered: ${this.deliveredPercent}%`
  failedTitle = `Failed: ${this.failedPercent}%`
  form: FormGroup;
  deliveryStatuses = ['All', 'Delivered', 'Failed'];
  countries = [];
  userCountries = [];
  emailChartTableData;
  emailTableData = [];
  disabledCountryOptions: Set<string> = new Set();
  disabledStatusOptions: Set<string> = new Set();

  constructor(
    private fb: FormBuilder,
    public qnsbnService: QnSbnService,
    private readonly oneSnackBar: OneSnackBarService,
    public cdr: ChangeDetectorRef,
    public readonly dialog?: MatDialog, 
  ){}

  ngOnInit() {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    this.userCountries = this.qnsbnService.getUserCountries();
    this.countries = [ 'All', ...this.userCountries ]
    this.form = this.fb.group(
      {
        country: [['All'], Validators.required],
        account: [''],
        deliveryStatus: [['Failed'], Validators.required],
        dateFrom: [thirtyDaysAgo, Validators.required],
        dateTo: [today, Validators.required]
      },
      { validators: dateRangeValidator() }
    );

    this.search();
    this.onCountryChange();
  }

  clearDate(controlName: string) {
    this.form.get(controlName)?.setValue(null);
  }

  clearAccount() {
    this.form.get('account')?.setValue('');
  }

  countrySelection(opts){
    if(opts.includes('All')){
      return this.userCountries
    }else{
      return opts
    }
  }

  search() {
    if (this.form.valid) {

      const searchObj = {
        "Country": this.countrySelection(this.form.get('country').value),
        "AccountIdName": this.form.get('account').value,
        "DeliveryStatus": this.form.get('deliveryStatus').value,
        "DateFrom": formatDateOnly(this.form.get('dateFrom').value),
        "DateTo": formatDateOnly(this.form.get('dateTo').value)
      }

      this.fetchEmailDetails(searchObj);

    } else {
      this.form.markAllAsTouched();
    }
  }

  fetchEmailDetails(obj){
    this.qnsbnService.emailReportDashboard(obj).subscribe(
      res => {
        this.emailChartTableData = res;
        this.emailTableData = [ ...this.emailChartTableData.emailResponse ];
        this.deliveredPercent = parseFloat(this.emailChartTableData.percentages.deliveredPct.toFixed(2));
        this.failedPercent = this.emailTableData.length === 0 ? 0 : parseFloat((100 - this.deliveredPercent).toFixed(2));
        this.delivered.nativeElement.style.flexBasis = `${this.deliveredPercent}%`;
        this.failed.nativeElement.style.flexBasis = `${this.failedPercent}%`;
      },
      err => {
        this.oneSnackBar.open('Please try again later', OneSnackBarType.ERROR, '', { duration: 3000 });
      }
    );
  }

  onDownload(): void {
    const downloadReport = this.dialog.open(FileFormatDialogComponent, { disableClose: true });
    downloadReport.afterClosed().subscribe(
      res => {
        if (res === 'csv') {
          this.csvData();
        }
        if (res === 'xlsx') {
          this.downloadExcel(this.emailTableData, 'EmailReport');
        }
      },
      err => {
        this.oneSnackBar.open('Please try again later', OneSnackBarType.ERROR, '', { duration: 3000 });
      }
    );
  }
  
  downloadExcel(data, filename) {
    const wb = XLSX.utils.book_new();
    const emailReport = data.map((row, index) => {
      const { checked, ...rest } = row;
      return { 'S.No': index + 1, ...rest };
    });
    const ws = XLSX.utils.json_to_sheet(emailReport);
    XLSX.utils.book_append_sheet(wb, ws, 'EmailReport');
    
    // Download File
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
    const blob = new Blob([this.stringToArrayBuffer(wbout)], { type: 'application/octet-stream' });

    let dwldLink = document.createElement('a');
    let url = URL.createObjectURL(blob);

    let isSafariBrowser = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1;
    if (isSafariBrowser) {
      dwldLink.setAttribute('target', '_blank');
    }

    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename + '.xlsx');
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }

  stringToArrayBuffer(s) {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) {
        view[i] = s.charCodeAt(i) & 0xFF;
    }
    return buf;
  }

  downloadCSV(data, filename, headerList) {
    let csvData = this.formatCSV(data, headerList);
    let blob = new Blob(['\uFEFF' + csvData], { type: 'text/csv;charset=utf-8;' });
    let dwldLink = document.createElement('a');
    let url = URL.createObjectURL(blob);
    let isSafariBrowser = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1;
    if (isSafariBrowser) {
      dwldLink.setAttribute('target', '_blank');
    }
    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename + '.csv');
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }

  formatCSV(objArray, headerList) {
    let str = '';
    str += headerList + '\r\n';

    for (let i = 0; i < objArray.length; i++) {
      let line = '';
      line += objArray[i] || '';
      str += line + '\r\n';
    }

    let res = '';
    res = 'SEP=,' + '\r\n' + str;

    return res;
  }
  
  csvData(){
    const contacts = this.emailTableData.map((row, index) => {
      const { checked, ...rest } = row;
      return { 'S.No': index + 1, ...rest };
    });

    let headers = contacts.map((item) => Object.keys(item).toString());
    headers = Array.from(new Set(headers));
    
    const values = contacts.map((item) => {
      return Object.values(item).map(value => {
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          const escapedValue = value.replace(/"/g, '""');
          return `"${escapedValue}"`;
        }
        return value;
      }).join(',');
    });
    
    this.downloadCSV(values, 'EmailReport', headers);
  }

  onCountryChange(): void {
    const selected = this.form.get('country').value || [];

    if (selected.includes('All')) {
      this.form.get('country').setValue([...this.countries]);
      this.disabledCountryOptions = new Set(this.form.get('country').value.filter(opt => opt !== 'All'));
    } 
    
    if(!selected.includes('All')){
      const allSelected = selected.length === this.countries.length-1;
      const disabledOptions = this.disabledCountryOptions.size > 0;

      if (allSelected) {
        this.form.get('country').setValue([...this.countries]);
        this.disabledCountryOptions = new Set(this.form.get('country').value.filter(opt => opt !== 'All'));
      }

      if (disabledOptions) {
        this.form.get('country').reset();
        this.disabledCountryOptions.clear();
      }
    }

    this.cdr.detectChanges();
  }

  isCountryDisabled(option: string): boolean {
    return this.disabledCountryOptions.has(option);
  }

  onStatusChange(): void {
    const selected = this.form.get('deliveryStatus').value || [];

    if (selected.includes('All')) {
      this.form.get('deliveryStatus').setValue([...this.deliveryStatuses]);
      this.disabledStatusOptions = new Set(this.form.get('deliveryStatus').value.filter(opt => opt !== 'All'));
    } 
    
    if(!selected.includes('All')){
      const allSelected = selected.length === this.deliveryStatuses.length-1;
      const disabledOptions = this.disabledStatusOptions.size > 0;

      if (allSelected) {
        this.form.get('deliveryStatus').setValue([...this.deliveryStatuses]);
        this.disabledStatusOptions = new Set(this.form.get('deliveryStatus').value.filter(opt => opt !== 'All'));
      }

      if (disabledOptions) {
        this.form.get('deliveryStatus').reset();
        this.disabledStatusOptions.clear();
      }
    }

    this.cdr.detectChanges();
  }

  isStatusDisabled(option: string): boolean {
    return this.disabledStatusOptions.has(option);
  }

  public isAllSelected(FCN: string): boolean {
    const selected = this.form.get(FCN).value || [];
    if(FCN === 'country'){
      return selected.length === this.countries.length || selected.includes('All');
    }
    if(FCN === 'deliveryStatus'){
      return selected.length === this.deliveryStatuses.length || selected.includes('All');
    }
  }

  public selectedRolesDisplay(FCN: string){
    if (this.isAllSelected(FCN)) {
      return 'All';
    }
    return (this.form.get(FCN).value || []).join(', ');
  }

}
